import { omit } from "lodash";
import { getFromDataPath, mutateByPath } from "../../../utility";
import { ExpressionLogicalOperatorEnum, WhereClauseWithCompoundExpression } from "../../ars_ldm";
import {
  isSubClauseWithCompoundExpression,
  isSubClauseWithCondition,
  isWhereClause,
  isWhereClauseWithCompoundExpression,
} from "../../typeGuards";
import { ArsContextState, IArsActionRemoveNotFromWhereClause } from "../interface";

/**
 * Removes a NOT wrapper from a where clause and promotes its single sub-clause.
 *
 * @param state - Current ARS context state
 * @param action - Remove-NOT action
 * @returns Updated state with promoted sub-clause
 */
export const arsc_removeNotFromWhereClause = <T>(
  state: ArsContextState<T>,
  action: IArsActionRemoveNotFromWhereClause,
): ArsContextState<T> => {
  const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
  if (
    !whereClause ||
    !isWhereClauseWithCompoundExpression(whereClause) ||
    !whereClause.compoundExpression.whereClauses ||
    whereClause.compoundExpression.whereClauses.length > 1
  ) {
    throw new Error(`Invalid dataPath for REMOVE_NOT_FROM_WHERE_CLAUSE: ${action.dataPath}`);
  }
  if (whereClause.compoundExpression.logicalOperator !== ExpressionLogicalOperatorEnum.NOT) {
    throw new Error(
      `Cannot remove NOT operator from ${action.dataPath} because it is not a NOT clause`,
    );
  }
  if (whereClause.compoundExpression.whereClauses.length === 0) {
    throw new Error(
      `Cannot remove NOT operator from ${action.dataPath} because it has no sub-clauses`,
    );
  }

  const subClause = whereClause.compoundExpression.whereClauses[0];
  if (isSubClauseWithCondition(subClause)) {
    const newEntity = {
      ...omit(whereClause, "compoundExpression"),
      condition: subClause.condition,
      level: subClause.level - 1,
      order: whereClause.order,
    };

    return mutateByPath({ ...state, changed: true }, action.dataPath, newEntity);
  }

  if (isSubClauseWithCompoundExpression(subClause)) {
    const newWhereClause: WhereClauseWithCompoundExpression = {
      compoundExpression: subClause.compoundExpression,
      level: subClause.level - 1,
      order: whereClause.order,
    };

    return mutateByPath({ ...state, changed: true }, action.dataPath, newWhereClause);
  }

  throw new Error(`Cannot remove NOT operator from ${action.dataPath} - sub-clause is a reference`);
};
